import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'

import { platformAuthFetcher } from '../platform-api'
import { FeatureFlags } from '../platform-api.types'

const CACHE_KEY = 'platform-api-feature-flags'
const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes
const REFRESH_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes

interface CachedFlags {
  flags: FeatureFlags
  timestamp: number
}

const DEFAULT_FLAGS: FeatureFlags = {
  sensors: false,
  tasks: false,
  employees: false,
}

export interface FeatureFlagContextValue {
  flags: FeatureFlags
  ready: boolean
}

export const FeatureFlagContext = createContext<FeatureFlagContextValue>({
  flags: DEFAULT_FLAGS,
  ready: false,
})

function loadCachedFlags(): CachedFlags | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed: CachedFlags = JSON.parse(raw)
    if (!parsed.flags || typeof parsed.timestamp !== 'number') return null
    return parsed
  } catch {
    return null
  }
}

function saveCachedFlags(flags: FeatureFlags): void {
  try {
    const entry: CachedFlags = { flags, timestamp: Date.now() }
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry))
  } catch {
    // localStorage full or unavailable — non-critical
  }
}

interface FeatureFlagProviderProps {
  children: React.ReactNode
  enabled?: boolean
}

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({ children, enabled = true }) => {
  const cached = useMemo(() => loadCachedFlags(), [])
  const isCacheFresh = cached !== null && Date.now() - cached.timestamp < CACHE_TTL_MS

  const [flags, setFlags] = useState<FeatureFlags>(isCacheFresh ? cached.flags : DEFAULT_FLAGS)
  const [ready, setReady] = useState(isCacheFresh)

  // Only fetch if enabled (user is authenticated)
  const swrKey = enabled ? '/v1/internal/config/feature-flags' : null

  const { data, error } = useSWR<FeatureFlags>(swrKey, platformAuthFetcher, {
    refreshInterval: REFRESH_INTERVAL_MS,
    revalidateOnFocus: false,
    dedupingInterval: 60_000,
    // If we have fresh cache, don't block render on fetch
    fallbackData: isCacheFresh ? cached.flags : undefined,
  })

  const onData = useCallback((newFlags: FeatureFlags) => {
    setFlags(newFlags)
    saveCachedFlags(newFlags)
    setReady(true)
  }, [])

  useEffect(() => {
    if (data) {
      onData(data)
    }
  }, [data, onData])

  useEffect(() => {
    if (error && !ready) {
      // Fetch failed with no cache — default to all-false (fail-safe to legacy)
      setReady(true)
    }
  }, [error, ready])

  const value = useMemo(() => ({ flags, ready }), [flags, ready])

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>
}
